import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import RequestForm from './components/RequestForm';
import RequestsTable from './components/RequestsTable';
import ConversionModal from './components/ConversionModal';
import RequestDetailsModal from './components/RequestDetailsModal';
import ManagerAssignmentModal from './components/ManagerAssignmentModal';
import { supabase } from '../../lib/supabase';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const SupportRequestManagement = () => {
  const [activeTab, setActiveTab] = useState('manage');
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showManagerAssignmentModal, setShowManagerAssignmentModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [customers, setCustomers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

  const requestsScrollRef = useRef(null);
  const calendarScrollRef = useRef(null);

  useEffect(() => {
    fetchFormData();
    
    const customersSubscription = supabase
      ?.channel('customers-support-changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'customers' }, () => {
        fetchFormData();
      })
      ?.subscribe();

    const productsSubscription = supabase
      ?.channel('products-support-changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
        fetchFormData();
      })
      ?.subscribe();

    return () => {
      customersSubscription?.unsubscribe();
      productsSubscription?.unsubscribe();
    };
  }, []);

  const fetchFormData = async () => {
    try {
      setSyncing(true);

      const { data: customersData } = await supabase
        ?.from('customers')
        ?.select('id, name, email, phone')
        ?.order('name');

      setCustomers((customersData || [])?.map(c => ({
        value: c?.id,
        label: c?.name,
        description: c?.email
      })));

      const { data: serviceTypesData } = await supabase
        ?.from('ticket_service_types')
        ?.select('id, name, description')
        ?.eq('is_active', true)
        ?.order('display_order');

      setServiceTypes((serviceTypesData || [])?.map(st => ({
        value: st?.id,
        label: st?.name,
        description: st?.description
      })));

      const { data: productsData } = await supabase
        ?.from('products')
        ?.select('id, name, sku, price')
        ?.order('name');

      setProducts((productsData || [])?.map(p => ({
        value: p?.id,
        label: p?.name,
        description: `${p?.sku} - $${p?.price}`
      })));
      
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error fetching form data:', error?.message);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  const mockCustomers = [
    { value: 'CUST-001', label: 'Acme Corporation', description: 'acme@example.com' },
    { value: 'CUST-002', label: 'TechStart Industries', description: 'contact@techstart.com' },
    { value: 'CUST-003', label: 'Global Solutions Ltd', description: 'info@globalsolutions.com' },
    { value: 'CUST-004', label: 'Innovate Systems', description: 'support@innovatesys.com' },
    { value: 'CUST-005', label: 'Premier Manufacturing', description: 'service@premiermfg.com' },
    { value: 'CUST-006', label: 'Digital Dynamics', description: 'help@digitaldynamics.com' },
    { value: 'CUST-007', label: 'Enterprise Networks', description: 'admin@entnetworks.com' },
    { value: 'CUST-008', label: 'Quantum Technologies', description: 'contact@quantumtech.com' }
  ];

  const mockProducts = [
    { value: 'PROD-001', label: 'Industrial HVAC System', description: 'Climate control equipment' },
    { value: 'PROD-002', label: 'Electrical Panel Upgrade', description: 'Power distribution systems' },
    { value: 'PROD-003', label: 'Security Camera System', description: 'Surveillance equipment' },
    { value: 'PROD-004', label: 'Fire Suppression System', description: 'Safety equipment' },
    { value: 'PROD-005', label: 'Network Infrastructure', description: 'IT and communication systems' },
    { value: 'PROD-006', label: 'Backup Generator', description: 'Emergency power systems' },
    { value: 'PROD-007', label: 'Access Control System', description: 'Security and entry management' },
    { value: 'PROD-008', label: 'Water Treatment System', description: 'Filtration and purification' },
    { value: 'PROD-009', label: 'Compressed Air System', description: 'Pneumatic equipment' },
    { value: 'PROD-010', label: 'Lighting System Upgrade', description: 'LED and smart lighting' },
    { value: 'SERV-001', label: 'Annual Maintenance Contract', description: 'Yearly service package' },
    { value: 'SERV-002', label: 'Emergency Response Service', description: '24/7 urgent support' },
    { value: 'SERV-003', label: 'System Monitoring Service', description: 'Remote monitoring and alerts' },
    { value: 'SERV-004', label: 'Training and Documentation', description: 'Staff training services' }
  ];

  const mockTechnicians = [
    { value: 'TECH-001', label: 'John Martinez', description: 'HVAC Specialist - Available' },
    { value: 'TECH-002', label: 'Sarah Chen', description: 'Electrical Engineer - Available' },
    { value: 'TECH-003', label: 'Michael Johnson', description: 'Network Technician - Busy' },
    { value: 'TECH-004', label: 'Emily Rodriguez', description: 'Safety Inspector - Available' },
    { value: 'TECH-005', label: 'David Kim', description: 'Systems Integrator - Available' },
    { value: 'TECH-006', label: 'Lisa Thompson', description: 'Field Service Lead - Available' },
    { value: 'TECH-007', label: 'Robert Anderson', description: 'Maintenance Specialist - Busy' },
    { value: 'TECH-008', label: 'Jennifer Lee', description: 'Technical Consultant - Available' }
  ];

  const [mockRequests, setMockRequests] = useState([
    {
      id: 1,
      requestId: 'REQ-2026-001',
      customerId: 'CUST-001',
      customerName: 'Acme Corporation',
      customerEmail: 'acme@example.com',
      customerPhone: '(555) 123-4567',
      serviceType: 'Equipment Repair',
      priority: 'high',
      status: 'pending',
      statusLabel: 'Pending Review',
      description: 'Critical HVAC system failure in Building A causing temperature control issues.',
      products: [
        { name: 'Industrial HVAC System', category: 'Climate Control' },
        { name: 'Emergency Response Service', category: 'Support Services' }
      ],
      productsCount: 2,
      createdDate: '02/20/2026',
      createdTime: '09:15 AM',
      lastUpdated: '02/21/2026 at 2:30 PM',
      assignedManager: 'Robert Wilson',
      notes: 'Customer reports unusual noises from main compressor unit',
      scheduledDate: '2026-02-25'
    },
    {
      id: 2,
      requestId: 'REQ-2026-002',
      customerId: 'CUST-003',
      customerName: 'Global Solutions Ltd',
      customerEmail: 'info@globalsolutions.com',
      customerPhone: '(555) 234-5678',
      serviceType: 'Equipment Installation',
      priority: 'medium',
      status: 'approved',
      statusLabel: 'Approved',
      description: 'New security camera system installation for warehouse facility.',
      products: [
        { name: 'Security Camera System', category: 'Security Equipment' },
        { name: 'Network Infrastructure', category: 'IT Systems' }
      ],
      productsCount: 2,
      createdDate: '02/19/2026',
      createdTime: '11:30 AM',
      lastUpdated: '02/20/2026 at 10:15 AM',
      assignedManager: 'Jennifer Martinez',
      scheduledDate: '2026-02-26'
    },
    {
      id: 3,
      requestId: 'REQ-2026-003',
      customerId: 'CUST-005',
      customerName: 'Premier Manufacturing',
      customerEmail: 'service@premiermfg.com',
      customerPhone: '(555) 345-6789',
      serviceType: 'Preventive Maintenance',
      priority: 'low',
      status: 'in-progress',
      statusLabel: 'In Progress',
      description: 'Quarterly preventive maintenance for all production equipment.',
      products: [
        { name: 'Compressed Air System', category: 'Pneumatic Equipment' },
        { name: 'Backup Generator', category: 'Power Systems' }
      ],
      productsCount: 3,
      createdDate: '02/18/2026',
      createdTime: '08:45 AM',
      lastUpdated: '02/22/2026 at 9:00 AM',
      assignedManager: 'David Thompson',
      notes: 'Scheduled for next week, customer prefers morning appointments',
      scheduledDate: '2026-02-27'
    },
    {
      id: 4,
      requestId: 'REQ-2026-004',
      customerId: 'CUST-002',
      customerName: 'TechStart Industries',
      customerEmail: 'contact@techstart.com',
      customerPhone: '(555) 456-7890',
      serviceType: 'System Upgrade',
      priority: 'critical',
      status: 'approved',
      statusLabel: 'Approved',
      description: 'Urgent electrical panel upgrade required to support new manufacturing equipment.',
      products: [
        { name: 'Electrical Panel Upgrade', category: 'Power Distribution' }
      ],
      productsCount: 2,
      createdDate: '02/21/2026',
      createdTime: '02:00 PM',
      lastUpdated: '02/22/2026 at 8:30 AM',
      assignedManager: 'Robert Wilson',
      notes: 'Requires licensed electrician and city permit',
      scheduledDate: '2026-02-25'
    },
    {
      id: 5,
      requestId: 'REQ-2026-005',
      customerId: 'CUST-007',
      customerName: 'Enterprise Networks',
      customerEmail: 'admin@entnetworks.com',
      customerPhone: '(555) 567-8901',
      serviceType: 'Technical Consultation',
      priority: 'medium',
      status: 'converted',
      statusLabel: 'Converted to Ticket',
      description: 'Consultation needed for facility-wide lighting system upgrade to LED technology.',
      products: [
        { name: 'Lighting System Upgrade', category: 'Electrical Systems' }
      ],
      productsCount: 2,
      createdDate: '02/17/2026',
      createdTime: '10:20 AM',
      lastUpdated: '02/19/2026 at 3:45 PM',
      assignedManager: 'Jennifer Martinez',
      scheduledDate: '2026-02-28'
    },
    {
      id: 6,
      requestId: 'REQ-2026-006',
      customerId: 'CUST-004',
      customerName: 'Innovate Systems',
      customerEmail: 'support@innovatesys.com',
      customerPhone: '(555) 678-9012',
      serviceType: 'Safety Inspection',
      priority: 'high',
      status: 'pending',
      statusLabel: 'Pending Review',
      description: 'Annual fire suppression system inspection and testing required for compliance.',
      products: [
        { name: 'Fire Suppression System', category: 'Safety Equipment' }
      ],
      productsCount: 2,
      createdDate: '02/22/2026',
      createdTime: '07:30 AM',
      assignedManager: 'David Thompson',
      notes: 'Must be completed before end of month for certification renewal',
      scheduledDate: '2026-03-01'
    }
  ]);

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0)?.getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1)?.getDay();

  const getRequestsForDay = (day) => {
    const year = calendarDate?.getFullYear();
    const month = calendarDate?.getMonth();
    const dateStr = `${year}-${String(month + 1)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
    return mockRequests?.filter(r => r?.scheduledDate === dateStr);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-primary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'approved': return 'text-green-600 bg-green-50 border-green-200';
      case 'in-progress': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'converted': return 'text-purple-600 bg-purple-50 border-purple-200';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const prevMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedCalendarDay(null);
  };

  const nextMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedCalendarDay(null);
  };

  const scrollRequests = (direction) => {
    requestsScrollRef?.current?.scrollBy({ top: direction === 'up' ? -200 : 200, behavior: 'smooth' });
  };

  const scrollCalendar = (direction) => {
    calendarScrollRef?.current?.scrollBy({ top: direction === 'up' ? -200 : 200, behavior: 'smooth' });
  };

  const handleFormSubmit = (formData) => {
    const newRequest = {
      id: mockRequests?.length + 1,
      requestId: `REQ-2026-${String(mockRequests?.length + 1)?.padStart(3, '0')}`,
      customerId: formData?.customerId || 'NEW-CUST',
      customerName: formData?.isNewCustomer ? formData?.newCustomerName : mockCustomers?.find(c => c?.value === formData?.customerId)?.label || '',
      customerEmail: formData?.isNewCustomer ? formData?.newCustomerEmail : mockCustomers?.find(c => c?.value === formData?.customerId)?.description || '',
      customerPhone: formData?.isNewCustomer ? formData?.newCustomerPhone : '(555) 000-0000',
      serviceType: formData?.serviceType || '',
      priority: formData?.priority,
      status: 'pending',
      statusLabel: 'Pending Review',
      description: formData?.description,
      products: formData?.selectedProducts?.map(productId => {
        const product = mockProducts?.find(p => p?.value === productId);
        return {
          name: product?.label || '',
          category: product?.description || ''
        };
      }),
      productsCount: formData?.selectedProducts?.length,
      createdDate: new Date()?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      createdTime: new Date()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      assignedManager: 'System Auto-Assigned'
    };

    setMockRequests(prev => [newRequest, ...prev]);
    setSuccessMessage(`Request ${newRequest?.requestId} created successfully! Manager has been notified.`);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
    setActiveTab('manage');
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleEdit = (request) => {
    setSuccessMessage(`Edit functionality for ${request?.requestId} would be implemented here`);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleConvertToTicket = (request) => {
    setSelectedRequest(request);
    setShowConversionModal(true);
  };

  const handleConfirmConversion = (conversionData) => {
    setMockRequests(prev =>
      prev?.map(req =>
        req?.id === conversionData?.requestId
          ? { ...req, status: 'converted', statusLabel: 'Converted to Ticket' }
          : req
      )
    );
    setSuccessMessage(`Request ${selectedRequest?.requestId} successfully converted to ticket!`);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  const handleAssignManager = (request) => {
    setSelectedRequest(request);
    setShowManagerAssignmentModal(true);
  };

  const handleConfirmManagerAssignment = async (requestId, managerId) => {
    try {
      const { data: manager } = await supabase
        ?.from('personnel')
        ?.select('name')
        ?.eq('id', managerId)
        ?.single();

      if (!manager) throw new Error('Manager not found');

      setMockRequests(prev => prev?.map(req => 
        req?.id === requestId 
          ? { ...req, assignedManager: manager?.name }
          : req
      ));

      setSuccessMessage('Manager assigned successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error assigning manager:', error?.message);
      alert('Failed to assign manager. Please try again.');
    }
  };

  const year = calendarDate?.getFullYear();
  const month = calendarDate?.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const isToday = (day) => today?.getFullYear() === year && today?.getMonth() === month && today?.getDate() === day;

  const selectedDayRequests = selectedCalendarDay ? getRequestsForDay(selectedCalendarDay) : [];

  return (
    <>
      <Helmet>
        <title>Support Request Management - RIGJOCKEY</title>
        <meta name="description" content="Create and manage customer support requests, track status, and convert approved requests to actionable service tickets" />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background">
        <div className="max-w-full mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Headphones" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                  Support Request Management
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Create new requests and manage existing service inquiries
                </p>
              </div>
            </div>
          </div>

          {showSuccessMessage && (
            <div className="mb-6 bg-success/10 border border-success/20 rounded-lg p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-success">{successMessage}</p>
              </div>
              <button
                onClick={() => setShowSuccessMessage(false)}
                className="p-1 rounded hover:bg-success/20 transition-smooth"
              >
                <Icon name="X" size={16} color="var(--color-success)" />
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-1 mb-6">
            <div className="border-b border-border">
              <div className="flex overflow-x-auto">
                <button
                  onClick={() => setActiveTab('create')}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-smooth border-b-2 whitespace-nowrap ${
                    activeTab === 'create' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Plus" size={18} color="currentColor" />
                  <span>Create Request</span>
                </button>
                <button
                  onClick={() => setActiveTab('manage')}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-smooth border-b-2 whitespace-nowrap ${
                    activeTab === 'manage' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="List" size={18} color="currentColor" />
                  <span>Manage Requests</span>
                  <span className="ml-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    {mockRequests?.length}
                  </span>
                </button>
              </div>
            </div>

            {activeTab === 'create' && (
              <div className="p-4 md:p-6 lg:p-8">
                <RequestForm
                  onSubmit={handleFormSubmit}
                  customers={mockCustomers}
                  serviceTypes={serviceTypes}
                  products={mockProducts}
                />
              </div>
            )}
          </div>

          {/* Two-Column Layout for Manage tab */}
          {activeTab === 'manage' && (
            <div className="flex flex-col xl:flex-row gap-6">

              {/* LEFT COLUMN: Requests List */}
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                    <Icon name="ClipboardList" size={18} color="var(--color-primary)" />
                    Support Requests
                    <span className="text-sm font-normal text-muted-foreground">({mockRequests?.length})</span>
                  </h2>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => scrollRequests('up')}
                      className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                      title="Scroll up"
                    >
                      <Icon name="ChevronUp" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      onClick={() => scrollRequests('down')}
                      className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                      title="Scroll down"
                    >
                      <Icon name="ChevronDown" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                </div>
                <div
                  ref={requestsScrollRef}
                  className="overflow-y-auto rounded-xl border border-border bg-card"
                  style={{ maxHeight: '70vh' }}
                >
                  <RequestsTable
                    requests={mockRequests}
                    onViewDetails={handleViewDetails}
                    onEdit={handleEdit}
                    onConvertToTicket={handleConvertToTicket}
                  />
                </div>
              </div>

              {/* RIGHT COLUMN: Calendar / Schedule */}
              <div className="flex flex-col xl:w-[420px] 2xl:w-[480px] flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                    <Icon name="CalendarDays" size={18} color="var(--color-primary)" />
                    Schedule
                  </h2>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => scrollCalendar('up')}
                      className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                      title="Scroll up"
                    >
                      <Icon name="ChevronUp" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      onClick={() => scrollCalendar('down')}
                      className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors"
                      title="Scroll down"
                    >
                      <Icon name="ChevronDown" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                </div>
                <div
                  ref={calendarScrollRef}
                  className="overflow-y-auto rounded-xl border border-border bg-card"
                  style={{ maxHeight: '70vh' }}
                >
                  {/* Calendar Header */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={prevMonth}
                        className="p-2 rounded-lg hover:bg-muted transition-colors"
                      >
                        <Icon name="ChevronLeft" size={18} color="var(--color-muted-foreground)" />
                      </button>
                      <h3 className="text-base font-heading font-semibold text-foreground">
                        {MONTHS?.[month]} {year}
                      </h3>
                      <button
                        onClick={nextMonth}
                        className="p-2 rounded-lg hover:bg-muted transition-colors"
                      >
                        <Icon name="ChevronRight" size={18} color="var(--color-muted-foreground)" />
                      </button>
                    </div>
                    {/* Day headers */}
                    <div className="grid grid-cols-7 mb-2">
                      {DAYS_OF_WEEK?.map(day => (
                        <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: firstDay })?.map((_, i) => (
                        <div key={`empty-${i}`} />
                      ))}
                      {Array.from({ length: daysInMonth })?.map((_, i) => {
                        const day = i + 1;
                        const dayRequests = getRequestsForDay(day);
                        const isSelected = selectedCalendarDay === day;
                        const isTodayDay = isToday(day);
                        return (
                          <button
                            key={day}
                            onClick={() => setSelectedCalendarDay(isSelected ? null : day)}
                            className={`relative flex flex-col items-center justify-start p-1 rounded-lg min-h-[44px] transition-colors text-sm ${
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : isTodayDay
                                ? 'bg-primary/10 text-primary font-semibold' :'hover:bg-muted text-foreground'
                            }`}
                          >
                            <span className="text-xs font-medium">{day}</span>
                            {dayRequests?.length > 0 && (
                              <div className="flex flex-wrap gap-0.5 mt-0.5 justify-center">
                                {dayRequests?.slice(0, 3)?.map((req, idx) => (
                                  <span
                                    key={idx}
                                    className={`w-1.5 h-1.5 rounded-full ${getPriorityColor(req?.priority)}`}
                                  />
                                ))}
                                {dayRequests?.length > 3 && (
                                  <span className="text-[9px] text-muted-foreground">+{dayRequests?.length - 3}</span>
                                )}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selected Day Requests */}
                  {selectedCalendarDay ? (
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Icon name="Calendar" size={14} color="var(--color-primary)" />
                        {MONTHS?.[month]} {selectedCalendarDay}, {year}
                        <span className="text-xs text-muted-foreground font-normal">({selectedDayRequests?.length} request{selectedDayRequests?.length !== 1 ? 's' : ''})</span>
                      </h4>
                      {selectedDayRequests?.length === 0 ? (
                        <div className="text-center py-6">
                          <Icon name="CalendarX" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-40" />
                          <p className="text-sm text-muted-foreground">No requests scheduled for this day</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {selectedDayRequests?.map(req => (
                            <div
                              key={req?.id}
                              className="p-3 rounded-lg border border-border bg-background hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => handleViewDetails(req)}
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="text-xs font-mono font-medium text-primary">{req?.requestId}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getStatusColor(req?.status)}`}>
                                  {req?.statusLabel}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-foreground">{req?.customerName}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{req?.serviceType}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`w-2 h-2 rounded-full ${getPriorityColor(req?.priority)}`} />
                                <span className="text-xs text-muted-foreground capitalize">{req?.priority} priority</span>
                                {req?.assignedManager && (
                                  <>
                                    <span className="text-muted-foreground">·</span>
                                    <span className="text-xs text-muted-foreground">{req?.assignedManager}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Icon name="Clock" size={14} color="var(--color-primary)" />
                        Upcoming Requests
                      </h4>
                      <div className="space-y-3">
                        {mockRequests
                          ?.filter(r => r?.scheduledDate && r?.status !== 'converted')
                          ?.sort((a, b) => new Date(a?.scheduledDate) - new Date(b?.scheduledDate))
                          ?.slice(0, 5)
                          ?.map(req => (
                            <div
                              key={req?.id}
                              className="p-3 rounded-lg border border-border bg-background hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => handleViewDetails(req)}
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="text-xs font-mono font-medium text-primary">{req?.requestId}</span>
                                <span className="text-xs text-muted-foreground">
                                  {req?.scheduledDate ? new Date(req?.scheduledDate + 'T00:00:00')?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-foreground">{req?.customerName}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{req?.serviceType}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`w-2 h-2 rounded-full ${getPriorityColor(req?.priority)}`} />
                                <span className="text-xs text-muted-foreground capitalize">{req?.priority} priority</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
      <ConversionModal
        isOpen={showConversionModal}
        onClose={() => setShowConversionModal(false)}
        request={selectedRequest}
        technicians={mockTechnicians}
        onConfirm={handleConfirmConversion}
      />
      <RequestDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onAssignManager={handleAssignManager}
      />
      {showManagerAssignmentModal && selectedRequest && (
        <ManagerAssignmentModal
          isOpen={showManagerAssignmentModal}
          onClose={() => {
            setShowManagerAssignmentModal(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onConfirm={handleConfirmManagerAssignment}
        />
      )}
    </>
  );
};

export default SupportRequestManagement;